import flatten from 'flat';
import { FormErrors } from './form';

export interface FormErrorResponse<T extends Record<string, unknown> | never = never> {
  code: number;
  message: string;
  errors: T extends never ? T : FormErrors<T>;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function formatServerFormErrors<T extends Record<string, unknown>>({
  errors,
  labels,
}: {
  errors: FormErrors<T>;
  labels?: ErrorLabels<T>;
}): { [k: string]: string[] } {
  const flattenErrors = flatten(errors);
  const flattenLabels = labels ? flatten(labels) : null;

  // @ts-expect-error
  return Object.keys(flattenErrors).reduce((prev, key) => {
    // @ts-expect-error
    const value = flattenErrors[key];
    if (typeof value !== 'string') {
      return prev;
    }

    // eslint-disable-next-line no-param-reassign
    key = key.replace(/children\./g, '').replace(/\.?errors\.\d+$/, '');

    if (flattenLabels) {
      const labels = key.split('.').reduce(
        (_prev, _key) => {
          if (_key.match(/^\d+$/)) {
            // @ts-expect-error
            _prev.previousKeys.push('children');
          } else {
            // @ts-expect-error
            _prev.previousKeys.push(_key);
          }

          // eslint-disable-next-line no-param-reassign
          _key = _prev.previousKeys.join('.');
          if (flattenLabels.hasOwnProperty(_key)) {
            // @ts-expect-error
            _prev.labels.push(flattenLabels[_key]);
          } else if (flattenLabels.hasOwnProperty(_key + '._label_')) {
            // @ts-expect-error
            _prev.labels.push(flattenLabels[_key + '._label_']);
            // @ts-expect-error
            _prev.previousKeys.push('children');
            // eslint-disable-next-line no-param-reassign
            _key += '.children';
            if (flattenLabels.hasOwnProperty(_key + '._label_')) {
              // @ts-expect-error
              _prev.labels.push(flattenLabels[_key + '._label_']);
            }
          }

          return _prev;
        },
        { labels: [], previousKeys: [] },
      ).labels;

      // eslint-disable-next-line no-param-reassign
      key = labels.length > 0 ? labels.join(' ➤ ') : key.replace(/\./g, ' ➤ ');
    } else {
      // eslint-disable-next-line no-param-reassign
      key = key.replace(/\./g, ' ➤ ');
    }

    if (!prev.hasOwnProperty(key)) {
      prev[key] = [];
    }

    prev[key].push(value);

    return prev;
  }, {} as { [k: string]: string[] });
}

export type ErrorLabels<T extends Record<string, unknown>> = {
  [K in keyof T]?: T[K] extends Record<string, unknown> ? ErrorLabel<T[K]> : string;
};

export interface ErrorLabel<T> {
  _label_?: string;
  children?: T extends Array<any>
    ? ErrorLabel<T[number]>
    : T extends Record<string, unknown>
    ? ErrorLabels<T>
    : ErrorLabel<T>;
}
